// Full GitHub/Slack-style emoji shortcode set, sourced from node-emoji (1,570 entries).
// Includes :beer:, :beers:, :clinking_glasses:, :champagne: etc.

import { search as nodeEmojiSearch, get as nodeEmojiGet } from 'node-emoji'

export interface EmojiEntry {
  shortcode: string
  emoji: string
  name: string
}

// Tiny set of friendly aliases on top of node-emoji's canonical names so
// natural words ("cheers", "tick") still resolve. node-emoji uses
// :clinking_glasses: not :cheers: — we'd lose those.
const ALIASES: Record<string, string> = {
  cheers: '🥂',
  clink: '🥂',
  toast: '🥂',
  tick: '✅',
  check: '✅',
  x: '❌',
  cross: '❌',
  yes: '✅',
  no: '❌',
  plus1: '👍',
  minus1: '👎',
  shrug: '🤷',
  meh: '😐'
}

/**
 * Search for matching emojis by query. Prefix matches rank above substring matches.
 * Returns up to `limit` results.
 */
export function searchShortcodes(query: string, limit = 8): EmojiEntry[] {
  const q = query.toLowerCase().trim()
  const seen = new Set<string>()
  const startsWith: EmojiEntry[] = []
  const includes: EmojiEntry[] = []

  // Aliases first (most common)
  for (const [code, emoji] of Object.entries(ALIASES)) {
    if (!q || code.startsWith(q) || code.includes(q)) {
      const entry = { shortcode: code, emoji, name: code }
      if (!seen.has(emoji + code)) {
        seen.add(emoji + code)
        ;(code.startsWith(q) ? startsWith : includes).push(entry)
      }
    }
    if (startsWith.length >= limit) break
  }

  // node-emoji's canonical names
  if (startsWith.length < limit) {
    const results = nodeEmojiSearch(q || 'a') // empty string returns [] — pick a broad seed
    for (const r of results) {
      if (seen.has(r.emoji + r.name)) continue
      seen.add(r.emoji + r.name)
      const entry: EmojiEntry = { shortcode: r.name, emoji: r.emoji, name: r.name.replace(/_/g, ' ') }
      if (r.name.toLowerCase().startsWith(q)) startsWith.push(entry)
      else includes.push(entry)
      if (startsWith.length >= limit) break
    }
  }

  // When query is empty, seed with common picks
  if (!q) {
    const seeds = ['check', 'cross', 'tada', 'fire', 'pray', 'eyes', 'bug', 'rocket']
    return seeds.map(s => ({ shortcode: s, emoji: ALIASES[s] ?? nodeEmojiGet(s), name: s })).slice(0, limit)
  }

  return [...startsWith, ...includes].slice(0, limit)
}

/** Lookup by exact shortcode — used by the popover when committing a selection. */
export function emojiFor(shortcode: string): string | undefined {
  return ALIASES[shortcode] ?? nodeEmojiGet(shortcode)
}
